import type { LoggerService } from '@/services/abstract/LoggerService';
import type { Libp2pService } from '@/services/abstract/poc/Libp2pService';
import {
  type MessageHandlerResult,
  type MessageStats,
  MessagingService,
} from '@/services/abstract/poc/MessagingService';
import type { Logger } from '@/types/Logger';
import type { HeliaNode } from '@/types/poc/helia';
import type { LibP2pWithServices } from '@/types/poc/libp2p';
import type { MessageHandler, MessagePayload } from '@/types/poc/message';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isMessagePayload = (value: unknown): value is MessagePayload =>
  isRecord(value) &&
  typeof value.type === 'string' &&
  typeof value.sender === 'string' &&
  typeof value.id === 'string' &&
  typeof value.timestamp === 'number';

const isRefreshFilesMessage = (
  value: MessagePayload
): value is MessagePayload & { type: 'refreshFiles'; sixId: string } =>
  value.type === 'refreshFiles';

const isRefreshSixMessage = (
  value: MessagePayload
): value is MessagePayload & { type: 'refreshSix'; sixId: string } => value.type === 'refreshSix';

const isRefreshSixesMessage = (
  value: MessagePayload
): value is MessagePayload & { type: 'refreshSixes' } => value.type === 'refreshSixes';

const isRefreshFileKeysMessage = (
  value: MessagePayload
): value is MessagePayload & { type: 'refreshFileKeys' } => value.type === 'refreshFileKeys';

const isRefreshFilesToDeleteListMessage = (
  value: MessagePayload
): value is MessagePayload & { type: 'refreshFilesToDeleteList' } =>
  value.type === 'refreshFilesToDeleteList';

const isLogoutMessage = (value: MessagePayload): value is MessagePayload & { type: 'logout' } =>
  value.type === 'logout';

export class MessagingServiceImpl extends MessagingService {
  private readonly log: Logger;

  constructor(
    loggerService: LoggerService,
    private readonly libp2pService: Libp2pService,
    private readonly handlers: MessageHandler[]
  ) {
    super();
    this.log = loggerService.createLogger({ namespace: 'six3ro:p2p:messaging' });
  }

  setupSubscriptions(
    libp2pNode: LibP2pWithServices,
    heliaNode: HeliaNode | null,
    onMessage: (result: MessageHandlerResult, stats: MessageStats) => void,
    topics: string[]
  ): () => void {
    const unsubscribeFunctions: Array<() => void> = [];
    this.log.info(`Setting up subscriptions for topics: ${topics.join(', ')}`);

    const handleIncomingMessage = async (
      data: Uint8Array,
      topic: string,
      originPeerId: string | null
    ): Promise<void> => {
      const decodedMessage = new TextDecoder().decode(data);
      this.log.debug(`Message received on topic ${topic}: ${decodedMessage}`);

      let parsed: unknown = null;
      try {
        parsed = JSON.parse(decodedMessage);
      } catch {
        this.log.debug(`Dropped non-JSON message on topic ${topic}`);
        return;
      }

      if (!isRecord(parsed)) {
        this.log.debug(`Dropped non-object message on topic ${topic}`);
        return;
      }

      const parsedRecord = parsed;
      if (topic.includes('mesh-heartbeat') && parsedRecord.source === 'hap') {
        this.log.debug('Received HAP mesh heartbeat');
        return;
      }

      if (!isMessagePayload(parsedRecord)) {
        this.log.info(`Inbound message dropped — missing required fields on topic ${topic}`, {
          keys: Object.keys(parsedRecord),
        });
        return;
      }

      this.log.info('Inbound P2P message received', {
        topic,
        type: parsedRecord.type,
        sender: parsedRecord.sender,
        originPeerId,
        targetPeerId: parsedRecord.targetPeerId ?? null,
      });

      const stats: MessageStats = {
        topic,
        timestamp: typeof parsedRecord.timestamp === 'number' ? parsedRecord.timestamp : Date.now(),
        messageType: typeof parsedRecord.type === 'string' ? parsedRecord.type : 'unknown',
        originPeerId,
      };

      const result = await this.processMessage(topic, parsedRecord, libp2pNode, heliaNode);

      if (result !== null) {
        this.log.info(`Inbound P2P message routed as '${result.type}' on topic ${topic}`);
        onMessage(result, stats);
      } else {
        this.log.debug(`Inbound P2P message '${parsedRecord.type}' not routed on topic ${topic}`);
      }
    };

    for (const topic of topics) {
      const unsubscribe = this.libp2pService.subscribeToTopic(
        libp2pNode,
        topic,
        (data, originPeerId) => {
          void handleIncomingMessage(data, topic, originPeerId);
        }
      );
      unsubscribeFunctions.push(unsubscribe);
    }

    return () => {
      this.log.info('Cleaning up messaging subscriptions');
      unsubscribeFunctions.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }

  async processMessage(
    topic: string,
    message: MessagePayload,
    libp2pNode: LibP2pWithServices,
    heliaNode: HeliaNode | null
  ): Promise<MessageHandlerResult | null> {
    const myPeerId = libp2pNode.peerId.toString();
    if (message.sender === myPeerId) {
      this.log.debug(`Dropping self-sent message '${message.type}'`);
      return null;
    }

    if (
      message.targetPeerId != null &&
      message.targetPeerId.length > 0 &&
      message.targetPeerId !== myPeerId
    ) {
      this.log.debug(`Dropping message '${message.type}' targeted at peer ${message.targetPeerId}`);
      return null;
    }

    if (isRefreshFilesMessage(message)) {
      return {
        type: 'refreshFiles',
        data: { sixId: message.sixId, sender: message.sender, messageTimestamp: message.timestamp },
      };
    }

    if (isRefreshSixMessage(message)) {
      return {
        type: 'refreshSix',
        data: { sixId: message.sixId, sender: message.sender, messageTimestamp: message.timestamp },
      };
    }

    if (isRefreshSixesMessage(message)) {
      return {
        type: 'refreshSixes',
        data: { sender: message.sender, messageTimestamp: message.timestamp },
      };
    }

    if (isRefreshFileKeysMessage(message)) {
      return {
        type: 'refreshFileKeys',
        data: { sender: message.sender, messageTimestamp: message.timestamp },
      };
    }

    if (isRefreshFilesToDeleteListMessage(message)) {
      return {
        type: 'refreshFilesToDeleteList',
        data: { sender: message.sender, messageTimestamp: message.timestamp },
      };
    }

    if (isLogoutMessage(message)) {
      return {
        type: 'logout',
        data: { sender: message.sender, messageTimestamp: message.timestamp },
      };
    }

    const handler = this.handlers.find((candidate) => candidate.canHandle(topic, message));
    if (handler === undefined) {
      this.log.debug(`No handler found for message type ${message.type} on topic ${topic}`);
      return null;
    }

    return await handler.process(message, libp2pNode, heliaNode);
  }
}
